import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DndContext, pointerWithin, useDraggable, useDroppable } from '@dnd-kit/core';
import {
  Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Chip, CircularProgress,
  Divider, Stack, TextField, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, DragIndicator as DragIcon, MenuBook as ProgramIcon,
  Restaurant as BookIcon, PersonSearch as PersonSearchIcon, EditOutlined as EditIcon
} from '@mui/icons-material';
import {
  NUTRITION_PROGRAMS, RECIPE_BOOKS, CLIENT_INTAKE, ASSIGN_NUTRITION, UNASSIGN_NUTRITION,
  SAVE_CLIENT_INTAKE
} from 'api/graphql/nutrition';
import { roles } from 'common/constants/options';

const CLIENTS = gql`
  query DirectoryClients {
    users(role: CLIENT, first: 300, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
      data { id full_name photo }
    }
  }
`;

const INTAKE_FIELDS = [
  ['weight', 'kg'], ['height', 'm'], ['age', ''], ['work_activity', ''], ['goal', ''], ['rest', ''],
  ['food_habits', ''], ['preferences', ''], ['cheat_meals', ''], ['supplements', ''],
  ['dietary_restrictions', ''], ['medical_conditions', '']
];

// Los tres primeros son numéricos: al guardar hay que mandarlos como número, no como texto.
const NUMERIC_INTAKE_FIELDS = ['weight', 'height', 'age'];

const PER_PAGE = 200;

function PaletteItem({ kind, id, title, icon }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `${kind}-${id}`, data: { kind, id } });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined;
  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={1}
      {...attributes}
      {...listeners}
      sx={{ p: 1, borderRadius: '10px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised', cursor: 'grab', touchAction: 'none', opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 1500 : 'auto', position: 'relative' }}
    >
      <DragIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>{title}</Typography>
    </Stack>
  );
}
PaletteItem.propTypes = { kind: PropTypes.string.isRequired, id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, title: PropTypes.string.isRequired, icon: PropTypes.node.isRequired };

function ClientRow({ client, programs, books, onRemove, canEditIntake }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const [expanded, setExpanded] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: `client-${client.id}`, data: { userId: client.id } });
  const { data: intakeData, loading, refetch: refetchIntake } = useQuery(CLIENT_INTAKE, { variables: { user_id: client.id }, skip: !expanded, fetchPolicy: 'cache-and-network' });
  const intake = intakeData?.clientIntake;

  // Edición del intake por parte del admin: los datos no siempre los mete el cliente, a veces
  // llegan por teléfono o en plena sesión y alguien tiene que poder registrarlos.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saveIntake, { loading: savingIntake }] = useMutation(SAVE_CLIENT_INTAKE);

  const startEditing = () => {
    const next = {};
    INTAKE_FIELDS.forEach(([field]) => { next[field] = intake?.[field] ?? ''; });
    setDraft(next);
    setEditing(true);
  };

  const saveDraft = async () => {
    const input = {};
    INTAKE_FIELDS.forEach(([field]) => {
      const value = draft[field];
      if (value === '' || value == null) return;
      input[field] = NUMERIC_INTAKE_FIELDS.includes(field) ? Number(value) : value;
    });

    await saveIntake({ variables: { user_id: client.id, input } });
    await refetchIntake();
    setEditing(false);
  };

  return (
    <Accordion
      ref={setNodeRef}
      expanded={expanded}
      onChange={(_, e) => setExpanded(e)}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: 'surface.content', border: '1px solid', borderRadius: '14px !important', overflow: 'hidden',
        borderColor: isOver ? 'secondary.main' : 'hairline.main', boxShadow: isOver ? (th) => `0 0 0 2px ${th.palette.gold.soft}` : 'none',
        '&::before': { display: 'none' }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1 } }}>
        <Avatar src={client.photo || undefined} sx={{ width: 32, height: 32, mr: 1.5, fontSize: 14 }}>{client.full_name?.[0]}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>{client.full_name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {programs.length} {t('programsTab', { ns: 'nutrition', defaultValue: 'Programas' }).toLowerCase()} · {books.length} {t('recipeBooksTab', { ns: 'nutrition', defaultValue: 'Recetarios' }).toLowerCase()}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('programsTab', { ns: 'nutrition', defaultValue: 'Programas' })}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {programs.length > 0 ? programs.map((p) => (
                <Chip key={p.assignmentId} size="small" label={p.title} onDelete={() => onRemove(p.assignmentId)} sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'gold.main' } }} />
              )) : <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('recipeBooksTab', { ns: 'nutrition', defaultValue: 'Recetarios' })}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {books.length > 0 ? books.map((b) => (
                <Chip key={b.assignmentId} size="small" label={b.title} onDelete={() => onRemove(b.assignmentId)} sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'gold.main' } }} />
              )) : <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>}
            </Stack>
          </Box>

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('intakeViewerSubtitle', { ns: 'nutrition', defaultValue: 'Datos de admisión del cliente' })}</Typography>
            {canEditIntake && !editing && !loading && (
              <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={startEditing} sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
                {t('editClientIntake', { ns: 'nutrition', defaultValue: 'Editar datos del cliente' })}
              </Button>
            )}
          </Stack>
          {loading && !intake && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} sx={{ color: 'secondary.main' }} /></Box>}
          {!loading && !intake && !editing && <Typography variant="body2" sx={{ color: 'text.disabled' }}>{t('noIntake', { ns: 'nutrition', defaultValue: 'Este cliente aún no ha llenado sus datos.' })}</Typography>}

          {editing ? (
            <Stack spacing={1.5}>
              <Grid container spacing={1.5}>
                {INTAKE_FIELDS.map(([field]) => (
                  <Grid xs={12} sm={6} md={4} key={field}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline={!NUMERIC_INTAKE_FIELDS.includes(field)}
                      type={NUMERIC_INTAKE_FIELDS.includes(field) ? 'number' : 'text'}
                      label={t(field, { ns: 'nutrition' })}
                      value={draft[field] ?? ''}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [field]: e.target.value }))}
                    />
                  </Grid>
                ))}
              </Grid>
              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" color="primary" onClick={saveDraft} disabled={savingIntake} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={savingIntake ? <CircularProgress size={14} color="inherit" /> : null}>
                  {t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
                </Button>
                <Button color="inherit" onClick={() => setEditing(false)} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>
                  {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
                </Button>
              </Stack>
            </Stack>
          ) : (
            intake && (
              <Grid container spacing={1.5}>
                {INTAKE_FIELDS.map(([field, unit]) => (
                  <Grid xs={6} sm={4} md={3} key={field}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised', height: '100%' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, display: 'block' }}>{t(field, { ns: 'nutrition' })}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{intake[field] ? `${intake[field]}${unit ? ` ${unit}` : ''}` : '-'}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
ClientRow.propTypes = {
  client: PropTypes.object.isRequired,
  programs: PropTypes.array.isRequired,
  books: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
  canEditIntake: PropTypes.bool.isRequired
};

/**
 * Directorio de clientes: tabla de clientes vinculados con sus programas/recetarios y su intake;
 * se arrastran programas/recetarios desde la paleta sobre un cliente para asignarlos (drag-and-drop).
 */
function NutritionDirectory() {
  const { t } = useTranslation(['nutrition']);
  const [search, setSearch] = useState('');

  // Son datos de salud, así que editarlos queda reservado al admin. El entrenador los sigue
  // viendo, que es lo que necesita para armar el plan.
  const role = useSelector((state) => state.auth.user?.role);
  const canEditIntake = role === roles.administrator;

  const { data: clientsData } = useQuery(CLIENTS, { fetchPolicy: 'cache-first' });
  const { data: progData, refetch: refetchPrograms } = useQuery(NUTRITION_PROGRAMS, { variables: { first: PER_PAGE, page: 1, search: '' }, fetchPolicy: 'cache-and-network' });
  const { data: bookData, refetch: refetchBooks } = useQuery(RECIPE_BOOKS, { variables: { first: PER_PAGE, page: 1, search: '' }, fetchPolicy: 'cache-and-network' });
  const [assign] = useMutation(ASSIGN_NUTRITION);
  const [unassign] = useMutation(UNASSIGN_NUTRITION);

  const clients = clientsData?.users?.data ?? [];
  const programs = progData?.nutritionPrograms?.data ?? [];
  const books = bookData?.recipeBooks?.data ?? [];

  // Asociaciones DIRECTAS (a cliente) por id de cliente, con el id de la asignación para quitarla.
  const assocByClient = useMemo(() => {
    const map = {};
    const add = (items, bucket) => items.forEach((it) => {
      (it.assignments_info ?? []).forEach((a) => {
        if (!a.user_id) return;
        if (!map[a.user_id]) map[a.user_id] = { programs: [], books: [] };
        map[a.user_id][bucket].push({ id: it.id, title: it.title, assignmentId: a.id });
      });
    });
    add(programs, 'programs');
    add(books, 'books');
    return map;
  }, [programs, books]);

  const visibleClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (q) return (c.full_name || '').toLowerCase().includes(q);
      const a = assocByClient[c.id];
      return a && (a.programs.length || a.books.length);
    });
  }, [clients, assocByClient, search]);

  const refetchAll = () => Promise.all([refetchPrograms(), refetchBooks()]);

  const onDragEnd = async ({ active, over }) => {
    if (!over || !active?.data?.current) return;
    const { kind, id } = active.data.current;
    const userId = over.data.current?.userId;
    if (!userId) return;
    await assign({
      variables: {
        nutrition_program_id: kind === 'program' ? id : null,
        recipe_book_id: kind === 'book' ? id : null,
        user_id: userId,
        plan_id: null
      }
    });
    await refetchAll();
  };

  const onRemove = async (assignmentId) => {
    await unassign({ variables: { id: assignmentId } });
    await refetchAll();
  };

  return (
    <DndContext collisionDetection={pointerWithin} onDragEnd={onDragEnd}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('directoryHint', { ns: 'nutrition', defaultValue: 'Arrastra un programa o recetario sobre un cliente para asignárselo. Expande un cliente para ver sus datos y quitar asociaciones.' })}
      </Typography>

      <Grid container spacing={2.5}>
        {/* Paleta */}
        <Grid xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('programsTab', { ns: 'nutrition', defaultValue: 'Programas' })}</Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {programs.length > 0 ? programs.map((p) => (
                <PaletteItem key={p.id} kind="program" id={p.id} title={p.title} icon={<ProgramIcon fontSize="small" sx={{ color: 'gold.main' }} />} />
              )) : <Typography variant="caption" sx={{ color: 'text.disabled' }}>{t('noProgramsAdmin', { ns: 'nutrition', defaultValue: 'No hay programas. Crea el primero.' })}</Typography>}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('recipeBooksTab', { ns: 'nutrition', defaultValue: 'Recetarios' })}</Typography>
            <Stack spacing={1}>
              {books.length > 0 ? books.map((b) => (
                <PaletteItem key={b.id} kind="book" id={b.id} title={b.title} icon={<BookIcon fontSize="small" sx={{ color: 'gold.main' }} />} />
              )) : <Typography variant="caption" sx={{ color: 'text.disabled' }}>{t('noBooksAdmin', { ns: 'nutrition', defaultValue: 'No hay recetarios. Crea el primero.' })}</Typography>}
            </Stack>
          </Box>
        </Grid>

        {/* Clientes */}
        <Grid xs={12} md={8}>
          <TextField
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            label={t('searchClientIntake', { ns: 'nutrition', defaultValue: 'Buscar cliente' })}
            placeholder={t('searchPlaceholder', { ns: 'nutrition', defaultValue: 'Buscar...' })}
            sx={{ mb: 2 }}
          />
          {visibleClients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <PersonSearchIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {search ? t('noResults', { ns: 'nutrition', defaultValue: 'Sin resultados' }) : t('noLinkedClients', { ns: 'nutrition', defaultValue: 'Aún no hay clientes vinculados. Busca un cliente y arrástrale un programa o recetario.' })}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {visibleClients.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  programs={assocByClient[c.id]?.programs ?? []}
                  books={assocByClient[c.id]?.books ?? []}
                  onRemove={onRemove}
                  canEditIntake={canEditIntake}
                />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </DndContext>
  );
}

export default NutritionDirectory;
